<template>
  <div ref="pages" class="pages" :data-dragscroll="drag" v-dragscroll="drag" @touchmove.prevent>
    <div v-if="spread" class="pages-wrapper" :data-loaded="loaded" :style="wrapperStyle">
      <Page class="even" @loaded="evenLoaded = true" v-if="evenPage" :max-height="maxHeight" :max-width="maxWidth" :page="evenPage" />
      <Page class="odd" @loaded="oddLoaded = true" v-if="oddPage" :max-height="maxHeight" :max-width="maxWidth" :page="oddPage" />
    </div>
    <div class="pages-wrapper" v-else :data-loaded="currentPageLoaded">
      <Page class="odd" @loaded="currentPageLoaded = true" v-if="currentPage" :max-height="maxHeight" :max-width="maxWidth" :page="currentPage" />
    </div>
  </div>
</template>

<script>
import Page from './Page'
import KEY from '@/utils/keycode'
import { mapGetters, mapActions } from 'vuex'
import { dragscroll } from 'vue-dragscroll'

export default {
  data () {
    return {
      width: null,
      height: null,
      evenLoaded: false,
      oddLoaded: false,
      currentPageLoaded: false
    }
  },
  created () {
    document.addEventListener('keydown', this.keyDown)
  },
  unmounted () {
    document.removeEventListener('keydown', this.keyDown)
    if (this._pagesResizeObserver) {
      this._pagesResizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updatePageBoard)
    }
  },
  mounted () {
    this.updatePageBoard()
    // Replaces `vue-resize`'s `<resize-observer>` component (its Vue 3 line has been stuck at
    // alpha since 2021) — a plain native `ResizeObserver` on the same element does the same job.
    // `ResizeObserver` isn't polyfilled by the legacy/nomodule bundle (`core-js` only covers
    // JS/ES features, not DOM APIs), and this repo's browserslist intentionally still targets
    // older school-managed hardware — fall back to a window resize listener when unavailable.
    if (typeof ResizeObserver !== 'undefined') {
      this._pagesResizeObserver = new ResizeObserver(this.updatePageBoard)
      this._pagesResizeObserver.observe(this.$refs.pages)
    } else {
      window.addEventListener('resize', this.updatePageBoard)
    }
  },
  methods: {
    updatePageBoard () {
      this.height = this.$el.offsetHeight
      this.width = this.$el.offsetWidth
    },
    keyDown (event) {
      const keyCode = event.keyCode
      if (keyCode === KEY.ARROW_LEFT) {
        this.goToPreviousPage()
      } else if (keyCode === KEY.ARROW_RIGHT) {
        this.goToNextPage()
      }
    },
    ...mapActions(['goToNextPage', 'goToPreviousPage', 'toggleSpread', 'setSpread'])
  },
  computed: {
    ...mapGetters([
      'spread',
      'evenPage',
      'oddPage',
      'currentPage',
      'zoom',
      'drawMode'
    ]),
    maxWidth () {
      let width = this.spread ? this.width / 2 : this.width
      if (this.zoom) {
        const pagesEl = this.$refs.pages
        width = width * 2 - (pagesEl.offsetWidth - pagesEl.clientWidth)
      }

      return width
    },
    maxHeight () {
      let height = this.height
      if (this.zoom) {
        const pagesEl = this.$refs.pages
        height = height * 2 - (pagesEl.offsetHeight - pagesEl.clientHeight)
      }

      return height
    },
    loaded () {
      if (this.spread) {
        return (!this.evenPage || this.evenLoaded) && (!this.oddPage || this.oddLoaded)
      }
    },
    pageNumber () {
      return Number(this.$route.params.pageNumber)
    },
    wrapperStyle () {
      if (this.zoom) {
        return {
          width: `${this.maxWidth * 2}px`
        }
      }
    },
    drag () {
      return this.drawMode !== 'draw' && this.zoom
    }
  },
  watch: {
    evenPage (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.evenLoaded = false
      } else {
        this.evenLoaded = true
      }
    },
    oddPage (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.oddLoaded = false
      } else {
        this.oddLoaded = true
      }
    },
    spread (newVal, oldVal) {
      if (!newVal) {
        this.currentPageLoaded = true
      } else {
        this.oddLoaded = true
        this.evenLoaded = true
      }
    },
    currentPage (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentPageLoaded = false
      } else {
        this.currentPageLoaded = true
      }
    },
    zoom () {
      this.$refs.pages.classList.toggle('zoom')
    }
  },
  components: {
    Page
  },
  directives: {
    dragscroll
  }
}
</script>

<style>
  .pages-wrapper {
    opacity: 0;
  }
  .pages-wrapper[data-loaded="true"] {
    opacity: 1;
    transition: opacity 0.5s;
  }
  .zoom[data-dragscroll="true"] {
    cursor : -webkit-grab;
    cursor : -moz-grab;
    cursor : -o-grab;
    cursor : grab;
  }
  .zoom[data-dragscroll="true"]:active {
    cursor : -webkit-grabbing;
    cursor : -moz-grabbing;
    cursor : -o-grabbing;
    cursor : grabbing;
  }
</style>
